/**
 * The load-bearing item: wires the code-entry and preview steps together and
 * makes `Join pool` actually call `POST /pool/join`, so a resolved invite
 * code results in a real membership row (S5). A preview that is never
 * followed by a join call is the defect this issue closes.
 *
 * This is deliberately one file at roughly the plan's 220-line guide. The
 * presentation lives in `JoinPoolPreview` (pure props), the pure rules in
 * `joinPoolFlow`, and the copy in `joinPoolErrorCopy`. What remains is one
 * component's phase state, which has no seam left to split without
 * prop-drilling a boundary no other screen in this app has.
 *
 * Error handling deliberately splits by step (see the plan's `## 5.6`):
 * preview-step failures render inline in the dialog; join-step failures go
 * through the shared API error bus, since a committed action that failed is
 * a real API failure, not an expected negative answer to a lookup.
 */
import { usePoolJoin, usePoolJoinPreview } from "@sportsmet/pool-sdk/private";
import { useAppAvailabilityGet } from "@sportsmet/site-sdk/public";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Button, Dialog, InlineBanner, Input } from "@sportsmet/ui";
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import { storePlatformFromUserAgent } from "../../shell/appStoreLinks";
import { GetTheAppBanner } from "./GetTheAppBanner";
import {
  canReadClipboard,
  PASTE_NO_CODE_MESSAGE,
  PASTE_REFUSED_MESSAGE,
} from "./joinPoolPaste";
import { JOIN_OFFLINE_MESSAGE, joinPoolErrorCopy } from "./joinPoolErrorCopy";
import {
  isJoinCodeComplete,
  joinCodeFromClipboard,
  newJoinRequestId,
  normalizeJoinCode,
  resolveJoinAvailability,
} from "./joinPoolFlow";
import {
  JoinPoolPreview,
  JoinPoolPreviewSkeleton,
  type typeJoinPoolPreviewModel,
} from "./JoinPoolPreview";
import { leagueAbbreviationsByKey } from "./poolsListState";

export type typeJoinPoolDialogProps = {
  open: boolean;
  /** From `?joinCode=`. When set and the dialog opens, the preview auto-resolves. */
  initialJoinCode?: string | undefined;
  onClose: () => void;
  /** Navigates to the pool. The dialog never navigates itself. */
  onGoToPool: (poolId: string) => void;
};

type typePhase = "code" | "preview" | "pending";

/**
 * ISSUE-913. The pool the preview resolved, held together with the code that
 * resolved it, in ONE state value.
 *
 * The two are stored as a pair because they are one fact: this pool, from this
 * code. Keeping the joined code in the invite-code field instead made the
 * dialog preview one pool and join another as soon as `?joinCode=` changed
 * while the dialog was open (a share link followed from `/pools`, which
 * `routes/join.$code.tsx` redirects into without ever unmounting the dialog).
 * There is no seam here for them to drift through now: nothing can put a pool
 * on screen without recording the code that produced it, and the Join pool
 * button reads that code rather than the field.
 */
type typeResolvedPool = {
  /** Exactly what was sent to `/pool/join-preview`, normalized. */
  joinCode: string;
  pool: typeJoinPoolPreviewModel;
};

export const JoinPoolDialog = (props: typeJoinPoolDialogProps): ReactNode => {
  const preview = usePoolJoinPreview();
  const join = usePoolJoin();
  // ISSUE-770: the preview's league badge reads the catalog's own
  // abbreviation rather than slicing `leagueKey`, matching the pool card.
  const catalog = useLeagueCatalogGet({});
  const leagueAbbreviations = useMemo(
    () => leagueAbbreviationsByKey(catalog.data?.data?.sports ?? []),
    [catalog.data],
  );

  const userAgent = navigator.userAgent;
  const storePlatform = storePlatformFromUserAgent(userAgent);
  const storeAvailability = useAppAvailabilityGet(
    {},
    { enabled: props.open && storePlatform !== null },
  );

  const [phase, setPhase] = useState<typePhase>("code");
  const [code, setCode] = useState(
    normalizeJoinCode(props.initialJoinCode ?? ""),
  );
  const [resolved, setResolved] = useState<typeResolvedPool | null>(null);
  const [inviterName, setInviterName] = useState<string | undefined>(undefined);
  const [availability, setAvailability] = useState<{
    canJoin: boolean;
    notice: string | null;
  } | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [isAlreadyMember, setIsAlreadyMember] = useState(false);
  // ISSUE-931. The URL invite code this open has already accounted for (the
  // complete ?joinCode= it opened with, or null). Not the last code
  // handlePreview was handed.
  const [accountedUrlJoinCode, setAccountedUrlJoinCode] = useState<
    string | null
  >(
    props.initialJoinCode !== undefined &&
      isJoinCodeComplete(props.initialJoinCode)
      ? normalizeJoinCode(props.initialJoinCode)
      : null,
  );
  const autoStarted = useRef(
    !(
      props.initialJoinCode !== undefined &&
      isJoinCodeComplete(props.initialJoinCode)
    ),
  );
  const codeInputRef = useRef<HTMLInputElement>(null);
  /** Set when a step transition put the code field back on screen. */
  const shouldFocusCodeField = useRef(false);

  /**
   * Drops everything the preview step learned and puts the fan back on the
   * invite code field. The typed code stays, because a fan who lands on the
   * wrong pool almost always got one character wrong and would rather fix it
   * than retype all eight.
   *
   * `autoStarted` is deliberately left alone. A deep link that already
   * resolved has set it, and clearing it here would let the effect below fire
   * the same preview again and bounce the fan straight back to the pool they
   * just backed out of.
   *
   * It also arms the focus flag the effect below reads, because the whole
   * point of this control is to retype one character.
   */
  const backToCodeStep = (): void => {
    shouldFocusCodeField.current = true;
    setPhase("code");
    setResolved(null);
    setInviterName(undefined);
    setAvailability(null);
    setIsAlreadyMember(false);
    setInlineError(null);
    setRequestId(null);
    preview.reset();
    join.reset();
  };

  /**
   * ISSUE-913. The dialog is mounted on `/pools` whether or not it is open, so
   * closing it does not unmount it and nothing clears itself. A fan who
   * mistyped a code and closed on somebody else's pool reopened onto that
   * same pool, with a Join pool button under it and no code field in sight.
   *
   * The reset runs when the dialog OPENS, not when it closes, and that is the
   * whole design. `open` is derived from `?dialog=` on the URL (see
   * `routes/pools.tsx`), so this dialog closes on paths it never hears about:
   * browser back, the phone's back gesture, and any navigation that drops the
   * param. Radix calls `onOpenChange` only for a close it mediated itself, so
   * a reset hung off the close paths misses the gesture fans use most. A
   * dialog that always opens fresh cannot carry anything in from ANY close,
   * including the ones nobody thought to enumerate.
   *
   * Resetting on the way in is also what keeps the deep link from re-arming
   * itself. `props.onClose()` navigates asynchronously, so for a render or two
   * after a close the dialog is still open with `?joinCode=` still on the URL.
   * Clearing `autoStarted` in that window let the effect below fire a second
   * preview and put the fan straight back on the pool they just left. Nothing
   * is cleared on close now, so `phase` stays `"preview"` and `autoStarted`
   * stays set all the way through it, and the effect has two separate reasons
   * not to fire.
   *
   * `code` is seeded from `props.initialJoinCode` as of the open, which is the
   * URL the fan is actually arriving on: empty when they pressed Join a pool,
   * the invite code when they followed a deep link.
   *
   * `isBannerDismissed` is NOT reset: that is the Get the App promo, and a fan
   * who waved it away should not have to wave it away again.
   *
   * A layout effect rather than a plain one because this has to land before
   * the browser paints. The reopened dialog animates in, and a passive effect
   * would let the stale pool show for a frame first, which is a shorter
   * version of the defect itself.
   */
  const wasOpen = useRef(props.open);
  useLayoutEffect(() => {
    const didJustOpen = props.open && !wasOpen.current;
    wasOpen.current = props.open;
    if (!didJustOpen) return;
    backToCodeStep();
    setCode(normalizeJoinCode(props.initialJoinCode ?? ""));
    const openedWithCompleteUrlCode =
      props.initialJoinCode !== undefined &&
      isJoinCodeComplete(props.initialJoinCode);
    // A fresh Join a pool open latches auto-start so a later share link is
    // offered; a deep-link open leaves it unlatched so the auto-start effect
    // still owns the opening code. ISSUE-931.
    autoStarted.current = !openedWithCompleteUrlCode;
    setAccountedUrlJoinCode(
      props.initialJoinCode !== undefined &&
        isJoinCodeComplete(props.initialJoinCode)
        ? normalizeJoinCode(props.initialJoinCode)
        : null,
    );
    // On an open, focus belongs to Radix: `Dialog` focuses the first field on
    // `onOpenAutoFocus`, which is the one moment that event fires. Disarm, so
    // the two never reach for the same field in the same commit.
    shouldFocusCodeField.current = false;
    // `backToCodeStep` is deliberately not a dependency: it is rebuilt every
    // render, and listing it would run this on every render instead of on the
    // open transition. The two props read here are the real dependency list.
    //
    // `props.initialJoinCode` is listed because the body reads it, not because
    // a change to it while the dialog is open does anything here: the guard
    // above drops that run, on purpose. Seeding the FIELD is all this does,
    // and a fan mid-type owns the field. What that arriving code resolves to
    // is the effect below, and what a join sends is `resolved.joinCode`.
  }, [props.open, props.initialJoinCode]);

  /**
   * Takes the code to resolve rather than reading the `code` state, because
   * the auto-resolve below runs in the SAME commit as the open reset that
   * seeds `code` from the deep link, and so still closes over the previous
   * value. `handleJoin` takes its code the same way, from `resolved`, so
   * neither step reads the field and neither can be handed a code the other
   * did not act on.
   */
  const handlePreview = async (joinCode: string): Promise<void> => {
    // ISSUE-931. The latch also starts true on a no-code open. After this
    // call it means this open has answered the current URL code.
    // accountedUrlJoinCode is the URL code as of this call (not the argument).
    // Both assignments stay above the first await: the accept handler in this
    // file puts phase back to "code" and then calls this, and the effect below
    // must already see the latch set before any render can run it.
    setAccountedUrlJoinCode(
      props.initialJoinCode !== undefined &&
        isJoinCodeComplete(props.initialJoinCode)
        ? normalizeJoinCode(props.initialJoinCode)
        : null,
    );
    autoStarted.current = true;
    setInlineError(null);
    let response;
    try {
      response = await preview.mutateAsync({ joinCode });
    } catch {
      setInlineError(JOIN_OFFLINE_MESSAGE);
      return;
    }
    if (!response.data) {
      setInlineError(joinPoolErrorCopy(response.error?.code ?? null));
      return;
    }
    const {
      pool,
      rulesSummary,
      isAlreadyMember: alreadyMember,
      membershipStatus,
      invitedByName,
    } = response.data;
    setResolved({
      joinCode,
      pool: {
        poolId: pool.poolId,
        name: pool.name,
        leagueKey: pool.leagueKey,
        contestType: pool.contestType,
        activeMemberCount: pool.activeMemberCount,
        rulesSummary,
      },
    });
    setInviterName(invitedByName ?? undefined);
    setAvailability(
      resolveJoinAvailability({
        isAlreadyMember: alreadyMember,
        membershipStatus: membershipStatus ?? null,
      }),
    );
    // The footer's "Go to pool" branch fires only when the resolved status
    // is truly "active" (the "You're already in this pool." notice) -- a
    // suspended, removed, rejected, or pending fan also has a membership
    // record (so the API's own `isAlreadyMember` may read true for them
    // too) but must see "Back to pools", not "Go to pool".
    setIsAlreadyMember(
      (membershipStatus ?? (alreadyMember ? "active" : null)) === "active",
    );
    setRequestId(newJoinRequestId());
    setPhase("preview");
  };

  /**
   * ISSUE-913. Takes the code the preview actually resolved, never the field.
   * Its only caller is the Join pool button, which renders inside the
   * `resolved !== null` branch below and passes `resolved.joinCode`, so the
   * code sent here is by construction the one that produced the pool the fan
   * is looking at. There is no reachable state where this runs without one:
   * no resolved pool means no preview step, which means no Join pool button.
   */
  const handleJoin = async (joinCode: string): Promise<void> => {
    let response;
    try {
      response = await join.mutateAsync({
        joinCode,
        requestId: requestId ?? newJoinRequestId(),
      });
    } catch {
      showOfflineError();
      return;
    }
    if (!response.data) {
      showApiError(response);
      return;
    }
    if (response.data.membershipStatus === "active") {
      props.onGoToPool(response.data.poolId);
      return;
    }
    setPhase("pending");
  };

  /**
   * ISSUE-1340. Reads what the fan copied (a code, a share link, or the whole
   * share message) and goes straight to the pool when it holds a full code,
   * the same as v1's "Paste my invite". A browser that refuses the read gets
   * an inline nudge to paste into the field by hand, never the error modal:
   * nothing on our side failed.
   */
  const handlePaste = async (): Promise<void> => {
    let pasted: string;
    try {
      pasted = await navigator.clipboard.readText();
    } catch {
      setInlineError(PASTE_REFUSED_MESSAGE);
      return;
    }
    const pastedCode = joinCodeFromClipboard(pasted);
    if (pastedCode === null) {
      setInlineError(PASTE_NO_CODE_MESSAGE);
      return;
    }
    setCode(pastedCode);
    await handlePreview(pastedCode);
  };

  useEffect(() => {
    if (!props.open) return;
    if (phase !== "code") return;
    if (props.initialJoinCode === undefined) return;
    if (!isJoinCodeComplete(props.initialJoinCode)) return;
    if (autoStarted.current) return;
    autoStarted.current = true;
    void handlePreview(normalizeJoinCode(props.initialJoinCode));
    // `handlePreview` intentionally is not a dependency: it closes over
    // state that changes every render, and re-running this effect on every
    // such change would defeat the `autoStarted` guard above. The four
    // conditions checked here are the real dependency list.
  }, [props.open, phase, props.initialJoinCode]);

  const isDeepLinkResolving =
    phase === "code" &&
    props.initialJoinCode !== undefined &&
    isJoinCodeComplete(props.initialJoinCode) &&
    preview.isPending;

  /**
   * ISSUE-931. Derived from the URL rather than stored. Auto-start owns only
   * the complete code this open opened with; a later arriving complete code
   * is offered whether or not this open has resolved anything. A leftover
   * ?joinCode= that this open already accounted for is not offered, which is
   * the close race and the "Use a different code" leftover together. A code
   * the fan typed is never touched.
   */
  const arrivingJoinCode =
    props.initialJoinCode !== undefined &&
    isJoinCodeComplete(props.initialJoinCode)
      ? normalizeJoinCode(props.initialJoinCode)
      : null;
  const offeredJoinCode =
    props.open &&
    arrivingJoinCode !== null &&
    arrivingJoinCode !== accountedUrlJoinCode
      ? arrivingJoinCode
      : null;

  const acceptOfferedJoinCode = (joinCode: string): void => {
    backToCodeStep();
    void handlePreview(joinCode);
  };

  /**
   * ISSUE-913. Moves focus to the invite code field when a step transition
   * puts it back on screen, which is the only way onto the code step that
   * `Dialog` cannot cover.
   *
   * `dialog-autofocus.mdc` is written about an overlay OPENING, and the rule
   * is handled once inside `Dialog`: it focuses the first field from Radix's
   * `onOpenAutoFocus`, an event that fires on open and never again. That is
   * the right home for the open case and this does not touch it. But "Use a
   * different code" is a step transition inside an overlay that is already
   * open, so no open event fires, `autoFocus={phase === "code"}` going false
   * to true reaches nothing, and the button the fan pressed unmounts under
   * their finger. Focus then falls back to the dialog container: no caret, and
   * on a phone no keyboard, in front of a field whose whole job is one
   * character. The rule's reason ("an extra tap to start typing") applies
   * unchanged here, so this is the same behavior extended to the one entrance
   * `Dialog` cannot see, not a second focus policy competing with it.
   *
   * The flag, rather than focusing straight from the click handler: the field
   * does not exist yet at click time. It mounts in the commit the phase change
   * causes, so the focus has to wait for that commit.
   *
   * `isDeepLinkResolving` is checked because the code step renders a skeleton
   * instead of the field while a deep-linked code is resolving. The flag stays
   * armed through that, and spends itself when the field is really there.
   */
  useEffect(() => {
    if (!shouldFocusCodeField.current) return;
    if (phase !== "code") return;
    if (isDeepLinkResolving) return;
    shouldFocusCodeField.current = false;
    codeInputRef.current?.focus();
  }, [phase, isDeepLinkResolving]);

  const banner = isBannerDismissed ? null : (
    <GetTheAppBanner
      userAgent={userAgent}
      availability={storeAvailability.data?.data ?? null}
      onDismiss={() => {
        setIsBannerDismissed(true);
      }}
    />
  );

  let body: ReactNode = null;
  let footer: ReactNode = null;

  if (phase === "code") {
    // No `maxLength` on the field: a browser cuts a paste down to the limit
    // BEFORE onChange sees it, so a pasted share link would arrive as its
    // first eight characters. `normalizeJoinCode` finds the code in the link
    // and caps the length itself (ISSUE-1340).
    body = isDeepLinkResolving ? (
      <JoinPoolPreviewSkeleton />
    ) : (
      <div className="flex flex-col gap-2">
        <Input
          ref={codeInputRef}
          label="Invite code"
          hint="Eight characters, or paste the invite link."
          value={code}
          onChange={(event) => {
            setCode(normalizeJoinCode(event.target.value));
          }}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          inputMode="text"
          data-testid="join-code-input"
          error={inlineError ?? undefined}
        />
        {canReadClipboard() ? (
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handlePaste()}
              data-testid="join-paste"
            >
              Paste invite
            </Button>
          </div>
        ) : null}
      </div>
    );
    footer = (
      <Button
        variant="primary"
        fullWidth
        loading={preview.isPending}
        disabled={!isJoinCodeComplete(code)}
        onClick={() => void handlePreview(code)}
        data-testid="join-find"
      >
        Find pool
      </Button>
    );
  } else if (phase === "preview" && resolved !== null) {
    body = (
      <>
        <JoinPoolPreview
          model={resolved.pool}
          inviterName={inviterName}
          leagueAbbreviations={leagueAbbreviations}
        />
        {availability?.notice ? (
          <InlineBanner variant="neutral" message={availability.notice} />
        ) : null}
      </>
    );
    // ISSUE-913. Every preview footer leads with this. Whatever it turned out
    // to be, the fan may simply have typed the wrong code, and until now the
    // only way off the preview step was to close the dialog. The footer
    // stacks bottom to top on a phone and runs left to right on a desktop, so
    // listing it first keeps it the least prominent control in either layout.
    const useDifferentCode = (
      <Button
        variant="secondary"
        onClick={backToCodeStep}
        data-testid="join-change-code"
      >
        Use a different code
      </Button>
    );
    if (availability?.canJoin) {
      footer = (
        <>
          {useDifferentCode}
          <Button variant="secondary" onClick={props.onClose}>
            Not now
          </Button>
          <Button
            variant="primary"
            loading={join.isPending}
            onClick={() => void handleJoin(resolved.joinCode)}
            data-testid="join-commit"
          >
            Join pool
          </Button>
        </>
      );
    } else if (isAlreadyMember) {
      footer = (
        <>
          {useDifferentCode}
          <Button
            variant="primary"
            onClick={() => {
              props.onGoToPool(resolved.pool.poolId);
            }}
            data-testid="join-go-to-pool"
          >
            Go to pool
          </Button>
        </>
      );
    } else {
      footer = (
        <>
          {useDifferentCode}
          <Button variant="secondary" onClick={props.onClose}>
            Back to pools
          </Button>
        </>
      );
    }
  } else if (phase === "pending" && resolved !== null) {
    body = (
      <>
        <p className="text-body-strong text-ink">You're on the list</p>
        <JoinPoolPreview
          model={resolved.pool}
          inviterName={inviterName}
          leagueAbbreviations={leagueAbbreviations}
        />
        <InlineBanner
          variant="warning"
          message="The Commissioner has to approve you before you can play. We'll let you know."
        />
      </>
    );
    footer = (
      <Button variant="secondary" onClick={props.onClose}>
        Back to pools
      </Button>
    );
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      title="Join a pool"
      description={
        phase === "code"
          ? "Ask the Commissioner for the pool's invite code."
          : undefined
      }
      autoFocus={phase === "code"}
      footer={footer}
    >
      {banner}
      {offeredJoinCode === null ? null : (
        <div data-testid="join-code-offer" className="mb-4">
          <InlineBanner
            variant="neutral"
            message={`There's a new invite code: ${offeredJoinCode}.`}
            action={{
              label: "Find this pool",
              onAction: () => {
                acceptOfferedJoinCode(offeredJoinCode);
              },
            }}
          />
        </div>
      )}
      {body}
    </Dialog>
  );
};
